// © Microsoft Corporation. All rights reserved.

import { LocalizationUtility } from "./LocalizationUtility";
import { TimestampType } from "./TimestampType";
import { getTimestampType, timespanInSeconds } from "./DateUtility";
import { LocalizableTimeStrings } from "./LocalizableTimeStrings";
import { DateTimeFormat } from "./DateTimeFormat";

/**
 * Class containing the main logic to transform date to responsive/readable string.
 */
export class ResponsiveDateTransformer {

    constructor(
        private timeLocStrings: LocalizableTimeStrings,
        private locale: string,
        private readonly locUtility: LocalizationUtility
    ) {}

    /**
     * Transforms the date into responsive readable format based on the current time and the date supplied to it.
     * Examples - returns string such as "Just Now", "2h ago", etc. based on the date provided and the current time.
     * @param dateFrom date that needs to be transformed
     * @param fallbackText text to use if browser doesn't support locale specifiers.
     * @param formatToUse format to use for conversion
     */
    public transform(dateFrom: Date, fallbackText: string, formatToUse: DateTimeFormat): string {
        const SECONDS_IN_ONE_HOUR = 3600;
        const SECONDS_IN_ONE_MINUTE = 60;
        const SECONDS_IN_ONE_DAY: number = 3600 * 24;

        if (!dateFrom) {
            return "";
        }

        if (!this.locUtility.supportsLocaleSpecifiers) {
            return fallbackText;
        }

        const timespan = timespanInSeconds(dateFrom);
        switch (getTimestampType(dateFrom)) {
            case TimestampType.None:
                return "";
            case TimestampType.JustNow:
                return this.timeLocStrings.JustNow;
            case TimestampType.LessThanOneHour:
                // if less than 2 minutes, it will round to 1 min ago
                return this.timespanString(timespan, timespan < 2 * SECONDS_IN_ONE_MINUTE ? this.timeLocStrings.NumberMinuteAgo : this.timeLocStrings.NumberMinutesAgo, SECONDS_IN_ONE_MINUTE);
            case TimestampType.Today:
                // if less than 2 hours, it will round to 1 hour ago
                return this.timespanString(timespan, timespan < 2 * SECONDS_IN_ONE_HOUR ? this.timeLocStrings.NumberHourAgo : this.timeLocStrings.NumberHoursAgo, SECONDS_IN_ONE_HOUR);
            case TimestampType.Yesterday:
                if (formatToUse === DateTimeFormat.Common) {
                    const formatString = this.timeLocStrings.TimeYesterday;
                    return this.locUtility.format(formatString, this.locUtility.formatTimeFromDate(dateFrom, this.locale));
                }
                if (timespan < SECONDS_IN_ONE_DAY) {
                    // if less than 2 hours, it will round to 1 hour ago
                    return this.timespanString(timespan, timespan < 2 * SECONDS_IN_ONE_HOUR ? this.timeLocStrings.NumberHourAgo : this.timeLocStrings.NumberHoursAgo, SECONDS_IN_ONE_HOUR);
                }
                // if less than 2 days, it will round to 1 day ago
                return this.timespanString(timespan, timespan < 2 * SECONDS_IN_ONE_DAY ? this.timeLocStrings.NumberDayAgo : this.timeLocStrings.NumberDaysAgo, SECONDS_IN_ONE_DAY);
            case TimestampType.ThisWeek:
                if (formatToUse === DateTimeFormat.Common) {
                    return this.locUtility.format(this.timeLocStrings.TimeDayOfWeek, this.locUtility.localizedWeekday(dateFrom, this.locale), this.locUtility.formatTimeFromDate(dateFrom, this.locale));
                }
                // if less than 2 days, it will round to 1 day ago
                return this.timespanString(timespan, timespan < 2 * SECONDS_IN_ONE_DAY ? this.timeLocStrings.NumberDayAgo : this.timeLocStrings.NumberDaysAgo, SECONDS_IN_ONE_DAY);
            case TimestampType.ThisYear:
                return this.locUtility.localizedMonthDate(dateFrom, this.locale);
            default:
                return this.locUtility.localizedDateStringMedium(dateFrom, this.locale);
        }
    }

    /**
     * Converts `timespan` into minutes, hours based on the `periodInSeconds`
     * and then formats the string to display the date-time in readable format.
     */
    private timespanString(timespan: number, locString: string, periodInSeconds: number): string {
        const displayNumber = Math.floor(timespan / periodInSeconds);
        return this.locUtility.format(locString, this.locUtility.localizedNumberString(displayNumber, this.locale));
    }
}